import { useState, useEffect, useCallback } from 'react';
import { UserSession } from '../lib/types';

const STORAGE_KEY = 'digestlog_auth_user';

const DEFAULT_USER: UserSession = {
  userId: 'usr_harry',
  accountId: 'Harry',
};

export function useAuth() {
  const [user, setUser] = useState<UserSession | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse cached auth user:', e);
    }
    // Default to Harry for personal app installation
    return DEFAULT_USER;
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Sync to localStorage whenever user state changes
  useEffect(() => {
    if (user) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      } catch (e) {
        console.error('Failed to save auth user to storage:', e);
      }
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user]);

  const login = useCallback(async (accountId: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/auth?action=login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: accountId.trim(), password: password.trim() }),
      });

      const data = await res.json() as { ok: boolean; user?: UserSession; error?: string };

      if (!res.ok || !data.ok || !data.user) {
        setAuthError(data.error || 'Invalid account ID or password');
        return false;
      }

      setUser(data.user);
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Network error during login';
      // Local fallback for offline mode if trying Harry
      if (accountId.trim().toLowerCase() === 'harry' && password.trim() === 'password') {
        const fallbackUser: UserSession = {
          userId: 'usr_harry',
          accountId: 'Harry',
        };
        setUser(fallbackUser);
        return true;
      }
      setAuthError(msg);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (accountId: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/auth?action=register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: accountId.trim(), password: password.trim() }),
      });

      const data = await res.json() as { ok: boolean; user?: UserSession; error?: string };

      if (!res.ok || !data.ok || !data.user) {
        setAuthError(data.error || 'Failed to create account');
        return false;
      }

      setUser(data.user);
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Network error during registration';
      setAuthError(msg);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setAuthError(null);
  }, []);

  const updateAccountName = useCallback(async (newName: string): Promise<boolean> => {
    if (!user) return false;
    setIsLoading(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/auth', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.userId,
        },
        body: JSON.stringify({ accountId: newName.trim() }),
      });

      const data = (await res.json()) as { ok: boolean; user?: UserSession; error?: string };

      if (!res.ok || !data.ok || !data.user) {
        setAuthError(data.error || 'Failed to update account name');
        return false;
      }

      setUser(data.user);
      return true;
    } catch {
      // Local fallback
      const updatedUser: UserSession = { ...user, accountId: newName.trim() };
      setUser(updatedUser);
      return true;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const deleteAccount = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    setIsLoading(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/auth', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.userId,
        },
      });

      const data = (await res.json()) as { ok: boolean; error?: string };

      if (!res.ok || !data.ok) {
        setAuthError(data.error || 'Failed to delete account');
        return false;
      }

      logout();
      return true;
    } catch {
      logout();
      return true;
    } finally {
      setIsLoading(false);
    }
  }, [user, logout]);

  return {
    user,
    userId: user?.userId || null,
    isLoading,
    authError,
    setAuthError,
    login,
    register,
    logout,
    updateAccountName,
    deleteAccount,
  };
}
